import { Box, Button, Flex, HStack, SimpleGrid, useToast, VStack } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { format } from 'date-fns'
import { useEffect } from 'react'
import { FieldError, SubmitHandler, useForm } from 'react-hook-form'
import InputMask from 'react-input-mask'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useHistory } from 'react-router'
import * as yup from 'yup'
import { Demand } from '../../../../Interfaces/demands'
import { Product } from '../../../../Interfaces/products'
import { RawMaterial } from '../../../../Interfaces/rawMaterial'
import { Input } from '../../../../components/Form/Input'
import { Select } from '../../../../components/Form/Select'
import { useAuth } from '../../../../context/AuthContext'
import { api } from '../../../../services/api'
import { accessOptions } from '../../../../utils/billingForms'
import { sortAlphabetically, toUpperCaseList } from './../../../../utils/util'

const createServiceOrderSchema = yup.object().shape({
  delivery_date: yup.string().required('Informe a data de entrega'),
  description: yup.string().required('Informe o nome do produto'),
  amount: yup.string().required('Informe a quantidade'),
  ink_color_id: yup.string().required('Informe a cor'),
  price: yup.string(),
  billing_method: yup.string(),
  step: yup.string().required(),
})

interface Props {
  currendDemand?: Demand
  onClose?: () => void
}

const formatDate = (date: Date) => {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

export default function ServiceCreateOrUpdate({ currendDemand, onClose }: Props) {
  const history = useHistory()
  const queryClient = useQueryClient()
  const toast = useToast()
  const { hasPermission } = useAuth()

  const canViewFinancialData = hasPermission('view_financial_data_on_demand')

  const { register, handleSubmit, formState, setValue, getValues } = useForm({
    resolver: yupResolver(createServiceOrderSchema),
  })

  useEffect(() => {
    const currentDate = formatDate(new Date())
    if (!getValues('delivery_date')) {
      setValue('delivery_date', currentDate)
    }
    setValue('delivery_date', currentDate)

    // Define automaticamente a etapa como "NOVO" para novas ordens de serviço
    setValue('step', 'NOVO')

    if (!canViewFinancialData && !getValues('billing_method')) {
      setValue('billing_method', accessOptions[0].value)
    }
    if (!canViewFinancialData && !getValues('price')) {
      setValue('price', '0.00')
    }
  }, [setValue, getValues, canViewFinancialData])

  const errors = formState.errors

  const { data: colors } = useQuery(
    'colors',
    async () => {
      const response = await api.get<RawMaterial[]>(`raw-materials`, {
        params: {
          category: 'TINTASCOLORIDAS',
          is_enable: true,
        },
      })

      const enabled = (response.data || []).filter((x: RawMaterial) => x.is_enable)

      const select = enabled.map((x: RawMaterial) => ({
        label: `${x.name} - ${x.amount}KG`,
        value: x.id,
      }))

      return {
        colors: select,
      }
    },
    {
      retry: false,
    },
  )

  const createDemand = useMutation(
    async (product: Product) => {
      const splitDate = product.delivery_date.split('/')

      const newDate = `${splitDate[1]}/${splitDate[0]}/${splitDate[2]}`

      const products = [{ 
        ...product, 
        delivery_date: format(new Date(newDate), 'yyyy-MM-dd'),
        oven_space: 1 // ✅ Definir queimada como 1/3 automaticamente
      }]

      const response = await api.post<Product>(`/demands/${currendDemand.id}/service-orders`, {
        products,
      })
      if (response && response.data && response.data.id) {
        toast({ description: 'Cadastrado com sucesso.' })
        onClose?.()
        history.push(`/demands/${currendDemand.id}/services_order/${response.data.id}`)
      } else {
        toast({ description: 'Erro ao obter ID da ordem de serviço', status: 'error' })
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('demands')
      },
      onError: ({ response }) => {
        const errorList = response?.data.errors

        if (errorList) {
          const toastOptions = errorList.map((err) => ({ description: err.message, status: 'error' }))
          toastOptions.forEach((t) => {
            toast(t)
          })
        } else {
          toast({ description: 'Falha ao cadastrar', status: 'error' })
        }
      },
    },
  )

  const handleCreateOrUpdate: SubmitHandler<Product> = async (values) => {
    await createDemand.mutateAsync(values)
  }

  return (
    <Box flex="1" borderRadius="8" bg="gray.800" as="form" onSubmit={handleSubmit(handleCreateOrUpdate)}>
      <VStack spacing="8">
        <SimpleGrid minChildWidth="240px" spacing="8" width="100%">
          <Input
            label="Data da Entrega"
            {...register('delivery_date')}
            error={errors.delivery_date as FieldError}
            as={InputMask}
            mask="99/99/9999"
          />
          <Input label="Nome do produto" {...register('description')} error={errors.description as FieldError} />
          <Input label="Quantidade" {...register('amount')} error={errors.amount as FieldError} />
          <Select
            label="Cores"
            {...register('ink_color_id')}
            options={sortAlphabetically(toUpperCaseList(colors?.colors))}
            placeholder="Selecione um cor"
            error={errors.ink_color_id as FieldError}
          />
        </SimpleGrid>
        <SimpleGrid minChildWidth="240px" spacing="8" width="100%">
          {canViewFinancialData && (
            <>
              <Input label="Valor" {...register('price')} error={errors.price as FieldError} />
              <Select
                label="Forma de cobrança"
                {...register('billing_method')}
                options={accessOptions}
                placeholder="Selecione"
                error={errors.billing_method as FieldError}
              />
            </>
          )}
        </SimpleGrid>
      </VStack>

      <Flex mt="8" justify="flex-end">
        <HStack spacing="4">
          <Button type="submit" colorScheme="pink" isLoading={formState.isSubmitting}>
            Salvar
          </Button>
        </HStack>
      </Flex>
    </Box>
  )
}
